import { ReactNode } from 'react';
import { Column } from 'react-table';
import { Box, styled, Typography, typographyClasses } from '@mui/material';
import { ContextMenuButton } from 'redesign';
import { IconDownload } from 'assets/img';
import { TFunctionNonStrict } from 'i18next';
import { DateTime } from 'luxon';
import { getDocumentStatusIcon } from './get-document-status-icon';
import { getDocumentNameLocisedKey, getDocumentTypeLocisedKey } from '../../utils';
import { DocumentsCellProps, DocumentsColumnDefinition } from '../documents-table.types';
import { useDisplayDocument } from '../hooks/use-display-document';

type DocumentNameCellContentProps = {
  t: TFunctionNonStrict<'web-common', undefined>;
  cell: DocumentsCellProps<'id'>;
};

export const getDocumentsColumnDefinition = (
  t: TFunctionNonStrict<'web-common', undefined>
): Column<DocumentsColumnDefinition>[] => {
  const flexWorkerName: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.flexworker_name', { ns: 'web-common' })}</Header>,
    accessor: 'flexWorkerName',
    Cell: FlexWorkerNameCell,
  } as Column<DocumentsColumnDefinition>;

  const employmentType: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.employment_type', { ns: 'web-common' })}</Header>,
    accessor: 'receiver',
    width: 200,
    Cell: EmploymentTypeCell,
  } as Column<DocumentsColumnDefinition>;

  const documentType: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.document_type', { ns: 'web-common' })}</Header>,
    accessor: 'type',
    width: 200,
    Cell: DocumentTypeCell(t),
  } as Column<DocumentsColumnDefinition>;

  const documentName: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.document_name', { ns: 'web-common' })}</Header>,
    accessor: 'id',
    width: 200,
    Cell: DocumentNameCell(t),
  } as Column<DocumentsColumnDefinition>;

  const creationDate: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.creation_date', { ns: 'web-common' })}</Header>,
    accessor: 'createdAt',
    width: 200,
    Cell: CreationDateCell,
  } as Column<DocumentsColumnDefinition>;

  const latestActionDate: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.latest_action_date', { ns: 'web-common' })}</Header>,
    accessor: 'updatedAt',
    width: 200,
    Cell: LatestActionCell,
  } as Column<DocumentsColumnDefinition>;

  const status: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.status', { ns: 'web-common' })}</Header>,
    accessor: 'status',
    width: 200,
    Cell: StatusCell(t),
  } as Column<DocumentsColumnDefinition>;

  const action: Column<DocumentsColumnDefinition> = {
    Header: () => <Header>{t('table_headers.action', { ns: 'web-common' })}</Header>,
    accessor: 'actions',
    width: 100,
    Cell: ActionCell,
  } as Column<DocumentsColumnDefinition>;

  return [
    flexWorkerName,
    employmentType,
    documentType,
    documentName,
    creationDate,
    latestActionDate,
    status,
    action,
  ];
};

const FlexWorkerNameCell = (cell: DocumentsCellProps<'flexWorkerName'>) => {
  return (
    <StyledFlexWorkerName variant="body1" noWrap>
      {cell.value}
    </StyledFlexWorkerName>
  );
};

const EmploymentTypeCell = (cell: DocumentsCellProps<'receiver'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value.type}
    </Typography>
  );
};

const DocumentTypeCell =
  (t: TFunctionNonStrict<'web-common', undefined>) => (cell: DocumentsCellProps<'type'>) => {
    return (
      <Typography variant="body1" noWrap>
        {t(getDocumentTypeLocisedKey(cell.value), { ns: 'hq-screen-specific' })}
      </Typography>
    );
  };

const DocumentNameCell =
  (t: TFunctionNonStrict<'web-common', undefined>) => (cell: DocumentsCellProps<'id'>) => {
    return <DocumentNameCellContent t={t} cell={cell} />;
  };

const DocumentNameCellContent = ({ t, cell }: DocumentNameCellContentProps) => {
  //passing t as prop for better performance and keeping cell lightweight
  const { id, type } = cell.row.original;
  const { handleDownload } = useDisplayDocument(id);

  return (
    <StyledLink as="button" onClick={handleDownload}>
      <StyledDownloadIcon />
      <Typography>{t(getDocumentNameLocisedKey(type), { ns: 'hq-screen-specific' })}</Typography>
    </StyledLink>
  );
};

const CreationDateCell = (cell: DocumentsCellProps<'createdAt'>) => {
  const formatted = DateTime.fromISO(cell.value).toFormat('d LLL yyyy');

  return (
    <BoxDateTime>
      <Typography variant="body1" color="secondary">
        {formatted}
      </Typography>
    </BoxDateTime>
  );
};

const LatestActionCell = (cell: DocumentsCellProps<'updatedAt'>) => {
  const formatted = DateTime.fromISO(cell.value).toFormat('d LLL yyyy');

  return (
    <BoxDateTime>
      <Typography variant="body1" color="secondary">
        {formatted}
      </Typography>
    </BoxDateTime>
  );
};

const StatusCell =
  (t: TFunctionNonStrict<'web-common'>) => (cell: DocumentsCellProps<'status'>) => {
    // t('documents.status.pending')
    // t('documents.status.accepted')
    // t('documents.status.signed')

    const key = `documents.status.${cell.value.toLowerCase()}`;

    return (
      <StyledStatusBox>
        <StyledIcon as={getDocumentStatusIcon(cell.value)} />
        <Typography variant="body1" noWrap>
          {t(key, { ns: 'web-common' })}
        </Typography>
      </StyledStatusBox>
    );
  };

const ActionCell = (cell: DocumentsCellProps<'actions'>) => {
  const actions = cell.getRowActions(cell.value.documentId);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`document-actions-menu-${cell.value.documentId}`}>
      {actions}
    </ContextMenuButton>
  );
};

type HeaderProps = {
  children: ReactNode;
};

const Header = ({ children }: HeaderProps) => (
  <Typography variant="body2" color="secondary" sx={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const StyledFlexWorkerName = styled(Typography)(() => ({
  [`&.${typographyClasses.root}`]: {
    fontWeight: 'bold',
  },
}));
const BoxDateTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
}));

const StyledStatusBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginRight: theme.spacing(1.5),
}));

const StyledLink = styled('a')(() => ({
  display: 'flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  padding: 0,
  textDecoration: 'none',
  cursor: 'pointer',
  textDecorationLine: 'underline',
}));

const StyledDownloadIcon = styled(IconDownload)(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginRight: theme.spacing(2),
}));
